/**
 * One instanced, camera-facing sprite pool per baked sheet — the drawable half
 * of `sprite-screen-size.ts`.
 *
 * **One draw call per sheet, not per frame rect.** A pool has to be able to
 * show any of its sheet's frames (`FLAK.BMP` has 31, `EXPLO.BMP` 18), and the
 * obvious way to get there with a stock `MeshBasicMaterial` is one
 * `InstancedMesh` per frame with the UV rect baked into its geometry — 61
 * meshes, 61 render pipelines, for at most a handful of sprites on screen.
 * Instead each instance carries its own UV rect as two instanced `vec2`
 * attributes and one small TSL `colorNode` samples the sheet through them,
 * which is one mesh, one pipeline and one draw call for the whole sheet
 * however many different frames are live. (three's WebGPU backend gives any
 * `InstancedBufferAttribute` on the geometry `stepMode: "instance"`
 * automatically, so `TSL.attribute` is all this needs — no custom node class.
 * Two `vec2`s rather than one `vec4` because `@types/three` types no swizzle
 * accessors, so `rect.xy` would only compile behind a cast.)
 *
 * **Hard mask, not alpha blending.** The bake writes palette index 0 as alpha
 * 0 and everything else as alpha 255 (`packages/baker/src/sprite-bake.ts`), so
 * the sheet's alpha is already binary and an `alphaTest` cut reproduces the
 * original's colour-keyed blit exactly. That keeps the material *opaque* —
 * depth-writing, unsorted, order-independent — which sidesteps the reversed-Z
 * `depthWrite: false` trap the 2026-08-19 cockpit lesson records and is the
 * more faithful of the two readings anyway: a 1990 blitter does not blend.
 * **This now holds for the `fade` path too** — see `sheetMaterial`, which is
 * where that rule was quietly broken until 2026-08-22.
 *
 * Nothing here allocates per frame. `begin`/`push`/`end` write straight into
 * the two instanced buffers through module-level scratch objects, the way
 * `flight/combat-visuals.ts` writes its bullet quads.
 */

import * as THREE from "three/webgpu";
import type { BakedModelSpriteSheet } from "./baked-model.ts";
import {
  emptyBillboardQuad,
  writeBillboardQuad,
  type BillboardLike,
  type MutableBillboardQuad,
} from "./model-sprites.ts";
import { ditherThreshold } from "./ordered-dither.ts";
import { metresPerScreenPixel, metresPerScreenPixelX } from "./sprite-screen-size.ts";

/** Binary sheet alpha means any cut in the open interval works; a half cut is the least surprising one. */
const SPRITE_ALPHA_TEST = 0.5;
/** Components per instance in each of the two UV attributes. */
const UV_COMPONENTS = 2;

const scratchQuad: MutableBillboardQuad = emptyBillboardQuad();
const scratchMatrix = new THREE.Matrix4();
const scratchPosition = new THREE.Vector3();
const scratchScale = new THREE.Vector3();
const cameraPosition = new THREE.Vector3();
const cameraQuaternion = new THREE.Quaternion();
const cameraScale = new THREE.Vector3();
const cameraRight = new THREE.Vector3();
const cameraUp = new THREE.Vector3();
const cameraForward = new THREE.Vector3();
// The camera basis above is module-level, i.e. **shared by every pool**. That
// is deliberate and safe because a frame places the camera once and every pool
// is begun against that same camera, so the last `begin` and every `push`
// afterwards agree; it is what keeps a three-pool frame to one decompose. A
// caller that ever wanted two cameras in one frame would have to move it into
// the instance.

function sheetTexture(sheet: BakedModelSpriteSheet): THREE.Texture {
  const texture = new THREE.TextureLoader().load(`/baked/${sheet.path}`);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  // NoColorSpace: the sheet is baked straight from the game's own palette
  // bytes, not a photograph — see `webgpu-support.ts`'s colour-management note.
  texture.colorSpace = THREE.NoColorSpace;
  return texture;
}

/**
 * **Every pool is opaque, fading ones included.**
 *
 * The `fade` path used to reach for `transparent: true` + `depthWrite: false`,
 * the exact pair the bullet-visibility lane measured rasterizing **zero
 * pixels** on a pooled `InstancedMesh` under reversed-Z on 2026-08-22
 * (`docs/engineering/lessons.md`, and `apps/web/test/bullet-material.test.ts`
 * for the tracer half of the same trap). That lane's closing rule was to go and
 * measure this pool, `SMOKE.BMP`'s, because it was built the same way.
 *
 * **Measured, and it did not fail the same way.** The puffs drew. What the
 * fatal pair cost here was the *fire*: a blended, depth-write-free smoke pool
 * renders after the opaque pass and paints its dark grey (`BARON.PAL` 64-74,
 * `#141414`-`#696969`) straight over the `EXPLO` flame anchored at the same
 * point, and the flame is the brighter, larger sprite of the two. Counted on a
 * real GPU over the warm fireball palette at 1280x800, same frame either side:
 * **397 flame pixels blended, 9 979 opaque** — a 25x difference, which is the
 * whole reason a burning aeroplane looked like a smudge instead of a fire
 * (`re-sessions/cockpit-window/shots/trail-{before,after}-chase.png`).
 *
 * The fade is re-expressed as a **cut**, which is also the more faithful
 * reading: `smoke.c` gives a puff a fixed 2.52 s life and then deletes it, and
 * `SMOKE.TBL`'s twelve LODs are a *distance* ladder with no alpha ramp
 * anywhere in the art (`flight/smoke-trail.ts`), so the original never blended
 * a puff out — a mode-13h blitter cannot. The dissolve keeps the port's own
 * softening of that pop while staying inside what the hardware of the day
 * could do: an ordered-dither stipple, opaque, depth-writing, order-
 * independent, cut by the same `alphaTest` every other pool uses. The mask
 * multiplies the sheet's own binary alpha, so a surviving texel is 1 and a
 * discarded one is 0 — nothing lands near the 0.5 cut and the `alphaTest` is
 * the same number for every pool.
 *
 * Exported only so `apps/web/test/screen-sprite-material.test.ts` can pin those
 * flags without a DOM — a pool's constructor loads its sheet through
 * `TextureLoader`, which needs one. Same arrangement as
 * `flight/combat-visuals.ts`'s `bulletTracerMaterial` and its own guard test.
 */
export function sheetMaterial(texture: THREE.Texture, fade: boolean): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  const origin = THREE.TSL.attribute<"vec2">("instanceUvOrigin", "vec2");
  const size = THREE.TSL.attribute<"vec2">("instanceUvSize", "vec2");
  material.colorNode = THREE.TSL.texture(texture, THREE.TSL.uv().mul(size).add(origin));
  material.alphaTest = SPRITE_ALPHA_TEST;
  if (fade) {
    material.opacityNode = THREE.TSL.step(ditherThreshold(), THREE.TSL.attribute<"float">("instanceAlpha", "float"));
  }
  return material;
}

function instancedAttribute(capacity: number, components: number): THREE.InstancedBufferAttribute {
  const attribute = new THREE.InstancedBufferAttribute(new Float32Array(capacity * components), components);
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}

export interface ScreenSpritePoolOptions {
  /**
   * Give every instance its own opacity, **stippled** rather than blended.
   *
   * Off by default, because a plain colour-key mask is what the original's
   * blitter does. The damage trail turns it **on**: `smoke-trail.ts` dissipates
   * a puff over its recovered 2.52 s life and the sheet has no fade of its own
   * (its twelve frames are a *distance* ladder, §4.3), so without a
   * per-instance opacity the six puffs of a steady-state trail would blink out
   * one at a time. The pool stays opaque either way — `sheetMaterial` explains
   * why that is not negotiable.
   */
  readonly fade?: boolean;
}

/**
 * A fixed-capacity pool of screen-sized sprites drawn from one sheet.
 *
 * Written as a class rather than a factory of closures: it holds six pieces of
 * mutable state and four methods, which is past the size the 2026-08-19
 * MULTIPLAYER lesson puts on the closure-factory pattern.
 */
export class ScreenSpritePool {
  readonly mesh: THREE.InstancedMesh;
  private readonly sheet: BakedModelSpriteSheet;
  private readonly uvOrigins: THREE.InstancedBufferAttribute;
  private readonly uvSizes: THREE.InstancedBufferAttribute;
  private readonly alphas: THREE.InstancedBufferAttribute | undefined;
  private readonly texture: THREE.Texture;
  private written = 0;
  private fovYRadians = 0;

  constructor(sheet: BakedModelSpriteSheet, capacity: number, options: ScreenSpritePoolOptions = {}) {
    this.sheet = sheet;
    this.texture = sheetTexture(sheet);
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.uvOrigins = instancedAttribute(capacity, UV_COMPONENTS);
    this.uvSizes = instancedAttribute(capacity, UV_COMPONENTS);
    geometry.setAttribute("instanceUvOrigin", this.uvOrigins);
    geometry.setAttribute("instanceUvSize", this.uvSizes);
    if (options.fade === true) {
      this.alphas = instancedAttribute(capacity, 1);
      geometry.setAttribute("instanceAlpha", this.alphas);
    }
    this.mesh = new THREE.InstancedMesh(geometry, sheetMaterial(this.texture, options.fade === true), capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  /**
   * Reads the camera's basis for this frame. It must already be placed — the
   * 2026-08-19 gunfire-geometry lesson's rule 4: a billboard built off a
   * stale camera faces last frame's attitude, and the identity on frame one.
   */
  begin(camera: THREE.Camera, fovYRadians: number): void {
    camera.updateWorldMatrix(true, false);
    camera.matrixWorld.decompose(cameraPosition, cameraQuaternion, cameraScale);
    cameraRight.set(1, 0, 0).applyQuaternion(cameraQuaternion);
    cameraUp.set(0, 1, 0).applyQuaternion(cameraQuaternion);
    // three's camera looks down its own -Z, so forward is -Z rotated into world.
    cameraForward.set(0, 0, -1).applyQuaternion(cameraQuaternion);
    this.fovYRadians = fovYRadians;
    this.written = 0;
  }

  /**
   * View-space depth of a world point under this frame's camera — the
   * quantity both the sizing rule and §3's `detail` ladder are functions of,
   * exposed so a caller can pick a LOD with the *same* number the sizing then
   * uses. Only meaningful between `begin` and `end`.
   */
  depthOf(x: number, y: number, z: number): number {
    return (
      (x - cameraPosition.x) * cameraForward.x +
      (y - cameraPosition.y) * cameraForward.y +
      (z - cameraPosition.z) * cameraForward.z
    );
  }

  /**
   * Places one sprite anchored at a world point. Returns `false` when the pool
   * is full, when the anchor is behind the lens, or when the billboard has no
   * drawable frame — a caller may treat all three the same way (draw nothing).
   */
  push(anchorX: number, anchorY: number, anchorZ: number, billboard: BillboardLike, opacity = 1): boolean {
    if (this.written >= this.mesh.instanceMatrix.count || opacity <= 0) return false;
    const depthM = this.depthOf(anchorX, anchorY, anchorZ);
    const metresPerPixel = metresPerScreenPixel(depthM, this.fovYRadians);
    if (metresPerPixel <= 0) return false;
    const perPixelX = metresPerScreenPixelX(depthM, this.fovYRadians);
    if (!writeBillboardQuad(scratchQuad, billboard, metresPerPixel, this.sheet, perPixelX)) return false;

    scratchPosition.set(
      anchorX + cameraRight.x * scratchQuad.centerX + cameraUp.x * scratchQuad.centerY,
      anchorY + cameraRight.y * scratchQuad.centerX + cameraUp.y * scratchQuad.centerY,
      anchorZ + cameraRight.z * scratchQuad.centerX + cameraUp.z * scratchQuad.centerY,
    );
    scratchScale.set(scratchQuad.width, scratchQuad.height, 1);
    scratchMatrix.compose(scratchPosition, cameraQuaternion, scratchScale);
    this.mesh.setMatrixAt(this.written, scratchMatrix);
    this.uvOrigins.setXY(this.written, scratchQuad.u0, scratchQuad.v0);
    this.uvSizes.setXY(this.written, scratchQuad.uSize, scratchQuad.vSize);
    this.alphas?.setX(this.written, opacity);
    this.written += 1;
    return true;
  }

  /** Publishes this frame's writes. A pool with nothing in it is hidden outright, so it costs no draw call at all. */
  end(): void {
    this.mesh.count = this.written;
    this.mesh.visible = this.written > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.uvOrigins.needsUpdate = true;
    this.uvSizes.needsUpdate = true;
    if (this.alphas) this.alphas.needsUpdate = true;
  }

  /** How many sprites the last `end()` published — what a draw-call count and the tests read. */
  get liveCount(): number {
    return this.written;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    // A material's `dispose` is what frees this render object's bindings under
    // the WebGPU backend (2026-08-19 world-stability lesson), so it is not
    // optional bookkeeping.
    (this.mesh.material as THREE.Material).dispose();
    this.texture.dispose();
  }
}
